import { useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import axios from "axios";
import {
  Monitor, Code2, Zap, Settings, Palette, Bot,
  Mail, MessageCircle, Phone, ArrowUp, Menu, X, CheckCircle, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  {
    id: "formatacao",
    icon: <Monitor size={28} />,
    title: "Formatação",
    description: "Formatação completa de computadores e notebooks com instalação de sistema operacional, drivers e softwares essenciais.",
    image: "https://images.unsplash.com/photo-1598187210738-6b10d7d0471a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
  },
  {
    id: "desenvolvimento",
    icon: <Code2 size={28} />,
    title: "Desenvolvimento",
    description: "Criação de sistemas, sites e aplicações personalizadas para atender às necessidades específicas do seu negócio.",
    image: "https://images.unsplash.com/photo-1593879621460-076d8c3e2878?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
  },
  {
    id: "automacao",
    icon: <Zap size={28} />,
    title: "Automação",
    description: "Automatize processos repetitivos, economize tempo e reduza erros com soluções de automação inteligentes.",
    image: "https://images.unsplash.com/photo-1759752393975-7ca7b302fcc6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
  },
  {
    id: "melhoria",
    icon: <Settings size={28} />,
    title: "Melhoria de Sistemas",
    description: "Otimização e atualização de sistemas existentes para melhorar performance, segurança e usabilidade.",
    image: "https://images.unsplash.com/photo-1680992044138-ce4864c2b962?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
  },
  {
    id: "logos",
    icon: <Palette size={28} />,
    title: "Criação de Logos",
    description: "Design profissional de identidade visual, logotipos e materiais gráficos que representam sua marca.",
    image: "https://images.pexels.com/photos/1086538/pexels-photo-1086538.jpeg?auto=compress&cs=tinysrgb&w=800"
  },
  {
    id: "chatbots",
    icon: <Bot size={28} />,
    title: "Venda de Chatbots",
    description: "Chatbots inteligentes para atendimento automatizado, geração de leads e suporte 24/7 ao seu cliente.",
    image: "https://images.unsplash.com/photo-1762328862557-e0a36587cd3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
  }
];

function FadeUp({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Header({ scrollToSection }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Início", id: "hero" },
    { label: "Serviços", id: "services" },
    { label: "Contato", id: "contact" }
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl shadow-sm border-b border-slate-200/50" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <button
          data-testid="logo-btn"
          onClick={() => scrollToSection("hero")}
          className="text-2xl font-black tracking-tighter text-slate-900 hover:text-blue-600 transition-colors duration-200"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Zenvyte
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-${link.id}`}
              onClick={() => scrollToSection(link.id)}
              className="text-slate-600 hover:text-blue-600 font-medium transition-colors duration-200 text-sm"
            >
              {link.label}
            </button>
          ))}
          <Button
            data-testid="nav-cta-btn"
            onClick={() => scrollToSection("contact")}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 py-2 text-sm font-semibold"
          >
            Fale Conosco
          </Button>
        </nav>

        <button
          data-testid="mobile-menu-btn"
          className="md:hidden text-slate-900 p-2"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {menuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-white border-t border-slate-100 px-6 py-4 space-y-3"
        >
          {navLinks.map((link) => (
            <button
              key={link.id}
              data-testid={`mobile-nav-${link.id}`}
              onClick={() => { scrollToSection(link.id); setMenuOpen(false); }}
              className="block w-full text-left text-slate-700 hover:text-blue-600 font-medium py-2 text-base"
            >
              {link.label}
            </button>
          ))}
          <Button
            data-testid="mobile-cta-btn"
            onClick={() => { scrollToSection("contact"); setMenuOpen(false); }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-2"
          >
            Fale Conosco
          </Button>
        </motion.div>
      )}
    </header>
  );
}

function Hero({ scrollToSection }) {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen bg-[#0F172A] flex items-center overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1746470427686-4c3551f3d689?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-transparent to-slate-900/80" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-20 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-blue-400 text-sm uppercase tracking-widest font-semibold mb-4"
          >
            Suporte de TI Profissional
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl md:text-6xl font-black tracking-tighter text-white leading-tight mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Soluções de TI que Impulsionam seu{" "}
            <span className="text-blue-400">Negócio</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-slate-300 text-base md:text-lg leading-relaxed mb-10 max-w-lg"
          >
            Da formatação ao desenvolvimento de sistemas, automação inteligente e chatbots. Entregamos tecnologia de ponta para transformar sua empresa.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button
              data-testid="hero-cta-btn"
              onClick={() => scrollToSection("contact")}
              size="lg"
              className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-4 text-base rounded-xl"
            >
              Solicitar Orçamento <ChevronRight size={18} className="ml-1" />
            </Button>
            <a
              data-testid="hero-whatsapp-btn"
              href="https://wa.me/554195138777"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-8 py-4 text-base rounded-xl transition-all duration-200"
            >
              <MessageCircle size={20} /> WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-12 flex flex-wrap gap-6"
          >
            {["Atendimento Rápido", "Suporte Dedicado", "Preço Justo"].map((item) => (
              <div key={item} className="flex items-center gap-2 text-slate-300 text-sm">
                <CheckCircle size={16} className="text-blue-400 flex-shrink-0" />
                {item}
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="hidden md:block"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1746470427686-4c3551f3d689?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Tecnologia Zenvyte"
              className="w-full h-[460px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Services({ scrollToSection }) {
  return (
    <section id="services" data-testid="services-section" className="py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6">
        <FadeUp className="text-center mb-16">
          <p className="text-blue-600 text-sm uppercase tracking-widest font-semibold mb-3">O que oferecemos</p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Nossos Serviços
          </h2>
          <p className="text-slate-500 text-base max-w-xl mx-auto">
            Soluções completas de tecnologia para você e sua empresa, do suporte ao desenvolvimento.
          </p>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <FadeUp key={service.id} delay={index * 0.1}>
              <div
                data-testid={`service-card-${service.id}`}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentNode.style.background = "#EFF6FF";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                      {service.icon}
                    </div>
                    <h3
                      className="text-xl font-semibold text-slate-900"
                      style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                    >
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-slate-500 text-sm leading-relaxed">{service.description}</p>
                  <button
                    data-testid={`service-cta-${service.id}`}
                    onClick={() => scrollToSection("contact")}
                    className="mt-4 text-blue-600 hover:text-blue-800 text-sm font-semibold flex items-center gap-1 transition-colors duration-200"
                  >
                    Solicitar <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Preencha todos os campos obrigatórios.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: "Mensagem enviada com sucesso!", description: "Entraremos em contato em breve." });
      setForm({ name: "", email: "", service: "", message: "" });
    } catch {
      toast({ title: "Erro ao enviar.", description: "Tente novamente ou nos contate pelo WhatsApp.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <FadeUp className="text-center mb-16">
          <p className="text-blue-600 text-sm uppercase tracking-widest font-semibold mb-3">Entre em contato</p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Pronto para transformar sua empresa?
          </h2>
          <p className="text-slate-500 text-base max-w-xl mx-auto">
            Fale com nossa equipe e receba um orçamento personalizado sem compromisso.
          </p>
        </FadeUp>

        <div className="grid md:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">
          <FadeUp delay={0.1}>
            <div className="space-y-8">
              <div>
                <h3
                  className="text-xl md:text-2xl font-semibold text-slate-900 mb-6"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  Informações de Contato
                </h3>
                <div className="space-y-5">
                  <a
                    data-testid="contact-email-link"
                    href="mailto:zenvyte@gmail.com"
                    className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50 hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 group"
                  >
                    <div className="p-3 bg-blue-100 rounded-lg text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-200">
                      <Mail size={22} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">E-mail</p>
                      <p className="text-slate-700 font-semibold">zenvyte@gmail.com</p>
                    </div>
                  </a>

                  <a
                    data-testid="contact-whatsapp-link"
                    href="https://wa.me/554195138777"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50 hover:bg-green-50 hover:border-green-200 transition-all duration-200 group"
                  >
                    <div className="p-3 bg-green-100 rounded-lg text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors duration-200">
                      <MessageCircle size={22} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">WhatsApp</p>
                      <p className="text-slate-700 font-semibold">+55 41 9513-8777</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50">
                    <div className="p-3 bg-blue-100 rounded-lg text-blue-600">
                      <Phone size={22} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">Atendimento</p>
                      <p className="text-slate-700 font-semibold">Segunda a Sexta, 8h - 18h</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.2}>
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="bg-[#F8FAFC] rounded-2xl p-8 border border-slate-200 space-y-5"
            >
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Nome <span className="text-red-500">*</span>
                </label>
                <Input
                  data-testid="contact-name-input"
                  placeholder="Seu nome completo"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-white border-slate-200 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  E-mail <span className="text-red-500">*</span>
                </label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  placeholder="seu@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-white border-slate-200 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Serviço de Interesse</label>
                <Select
                  value={form.service}
                  onValueChange={(val) => setForm({ ...form, service: val })}
                >
                  <SelectTrigger data-testid="contact-service-select" className="bg-white border-slate-200">
                    <SelectValue placeholder="Selecione um serviço" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="formatacao">Formatação</SelectItem>
                    <SelectItem value="desenvolvimento">Desenvolvimento</SelectItem>
                    <SelectItem value="automacao">Automação</SelectItem>
                    <SelectItem value="melhoria">Melhoria de Sistemas</SelectItem>
                    <SelectItem value="logos">Criação de Logos</SelectItem>
                    <SelectItem value="chatbots">Venda de Chatbots</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Mensagem <span className="text-red-500">*</span>
                </label>
                <Textarea
                  data-testid="contact-message-input"
                  placeholder="Descreva brevemente o que você precisa..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  className="bg-white border-slate-200 focus:border-blue-500 resize-none"
                />
              </div>
              <Button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl text-base"
              >
                {loading ? "Enviando..." : "Enviar Mensagem"}
              </Button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function Footer({ scrollToSection }) {
  return (
    <footer data-testid="footer" className="bg-[#0F172A] text-slate-400 py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <button
            onClick={() => scrollToSection("hero")}
            className="text-2xl font-black tracking-tighter text-white hover:text-blue-400 transition-colors duration-200"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Zenvyte
          </button>
          <p className="text-slate-500 text-sm text-center">
            © 2026 Zenvyte. Todos os direitos reservados.
          </p>
          <button
            data-testid="back-to-top-btn"
            onClick={() => scrollToSection("hero")}
            className="flex items-center gap-2 text-slate-400 hover:text-blue-400 text-sm font-medium transition-colors duration-200"
          >
            <ArrowUp size={16} /> Voltar ao topo
          </button>
        </div>
      </div>
    </footer>
  );
}

function App() {
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="zenvyte-app">
      <Header scrollToSection={scrollToSection} />
      <main>
        <Hero scrollToSection={scrollToSection} />
        <Services scrollToSection={scrollToSection} />
        <Contact />
      </main>
      <Footer scrollToSection={scrollToSection} />
      <Toaster />

      <a
        data-testid="floating-whatsapp-btn"
        href="https://wa.me/554195138777"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
        title="Falar no WhatsApp"
      >
        <MessageCircle size={24} />
      </a>
    </div>
  );
}

export default App;
